import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuthStore } from '@/auth/auth-store';

/**
 * "Session Expired" modal (F-AUT-089) — shown when a token refresh fails on
 * an active session (the auth-store sets `sessionExpired` in `doRefresh`'s
 * failure paths instead of logging out silently). OK completes the logout +
 * redirect to /login.
 *
 * Mirrors Flutter `showSessionExpiredPopup` (`session_helper.dart:194-207`):
 *   - Title: "Session Expired"  (l10n `sessionExpired`)
 *   - Body:  "Either You have been idle for more than 120 min or your session
 *            has expired, please login again to continue"
 *   - Single CTA "Ok" (`hideNo: true`) → `afterYes` = logout.
 *
 * Copy deviation from Flutter's literal l10n (intentional, ruled 2026-07-16):
 * Flutter's `eitherYouHaveBeenIdleForMoreThan15Min` string says "15 min" but
 * Flutter's own idle CODE caps at 120 min (`dio_api_client.dart:309-310`) — the
 * string is stale in Flutter itself. React mirrors Flutter's FUNCTIONALITY
 * (the 120-min idle cap added in this PR), so the copy states the true 120-min
 * value rather than copying the wrong number. Behaviour parity over literal-
 * string parity.
 *
 * Mounted once at app root. OK-only and non-dismissable via outside-click /
 * escape — matches Flutter `confirmDialog` barrier-block, and prevents the
 * user from dismissing the modal back onto a dead (token-invalid) session.
 */
export function SessionExpiredDialog() {
  const sessionExpired = useAuthStore((s) => s.sessionExpired);
  const confirmSessionExpired = useAuthStore((s) => s.confirmSessionExpired);

  // In-flight covers the logout round trip (UpdateDeviceDetails POST);
  // disables the button so a double-click can't fire two logouts.
  const [isInFlight, setIsInFlight] = useState(false);

  async function onConfirm() {
    if (isInFlight) return;
    setIsInFlight(true);
    try {
      await confirmSessionExpired();
    } finally {
      setIsInFlight(false);
    }
  }

  return (
    <AlertDialog open={sessionExpired}>
      <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <AlertDialogHeader>
          <AlertDialogTitle>Session Expired</AlertDialogTitle>
          <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
            Either You have been idle for more than 120 min or your session has
            expired, please login again to continue
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={(e) => {
              // Prevent base-ui's default close — confirmSessionExpired drives
              // the state flip (clear flag + logout + redirect).
              e.preventDefault();
              void onConfirm();
            }}
            disabled={isInFlight}
          >
            {isInFlight ? 'Logging out…' : 'Ok'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default SessionExpiredDialog;
